import { SchedulesReportView } from "@common/types/views/schedulesReportView";
import React from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ViewOptions } from "@common/types/views/views";
import { SchedulesReportResponseWithPresentation } from "@common/schemas/domains/accounting/schedules-report.schema";
import { ReportPresentationChrome } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { AccountingReportFilters } from "../types";
import {
    AccountingReportContext,
    useAccountingReportContextValue,
} from "../AccountingReport.context";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { AccountingReportFooter } from "../AccountingReportFooter";
import { useReportChrome } from "../useReportChrome";
import { SchedulesReportTable } from "./SchedulesReportTable";
import { SchedulesReportTableCallbacks } from "./schedulesReportDrillDown.lib";
import { SchedulesReportViewRow } from "./schedulesReportViewTypes";
import styles from "./SchedulesReportPageContent.module.scss";

interface Props {
    data: SchedulesReportResponseWithPresentation;
    rows: SchedulesReportViewRow[];
    filters: AccountingReportFilters;
    chrome: ReportPresentationChrome;
    numberFormat: NumberFormatValues;
    generationDate: Date;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    tableRef: React.RefObject<ViewTableHandle | null>;
    callbacks: SchedulesReportTableCallbacks;
    viewOptions?: ViewOptions | null;
    viewColumns?: SchedulesReportView["columns"];
}

export const SchedulesReportPageContent: React.FC<Props> = ({
    data,
    rows,
    filters,
    chrome,
    numberFormat,
    generationDate,
    expanded,
    onExpandedChange,
    tableRef,
    callbacks,
    viewOptions,
    viewColumns,
}) => {
    const resolvedChrome = useReportChrome({ chrome, generationDate });
    const accountingReportContext = useAccountingReportContextValue(
        filters,
        numberFormat,
    );
    const hasRows = data.rows.length > 0;

    return (
        <Card stacked sharpEdges fullPage>
            <AccountingReportContext.Provider value={accountingReportContext}>
                <CardSection className={styles.header}>
                    <AccountingReportHeader chrome={resolvedChrome} />
                </CardSection>
                {hasRows && (
                    <SchedulesReportTable
                        report={data}
                        rows={rows}
                        expanded={expanded}
                        onExpandedChange={onExpandedChange}
                        tableRef={tableRef}
                        callbacks={callbacks}
                        viewOptions={viewOptions}
                        viewColumns={viewColumns}
                    />
                )}
                {!hasRows && (
                    <CardSection className={styles.emptyState}>
                        <EmptyState
                            header="No recognition schedules in this range"
                            body="Revenue schedules applied on this ledger will appear here once their activity falls within the selected dates."
                        />
                    </CardSection>
                )}
                {data.meta.footnotes.map((footnote) => (
                    <p key={footnote} className={styles.footnote}>
                        {footnote}
                    </p>
                ))}
                <AccountingReportFooter chrome={resolvedChrome} />
            </AccountingReportContext.Provider>
        </Card>
    );
};
